// Parsea texto que el usuario tipeó en un AmountInput a un string decimal
// canónico que el backend acepta (regex `\d+(\.\d{1,2})?`, sin negativos).
//
// Inputs aceptados:
//   "2500000"        → "2500000.00"
//   "2500000.5"      → "2500000.50"
//   "2,500,000"      → "2500000.00"  (separador de miles tipo CR/EN)
//   "2.500.000,50"   → "2500000.50"  (formato CR canónico)
//   "₡1.500.000"     → "1500000.00"  (con símbolo)
//   ""               → null
//   "abc"            → null
//   "-100"           → null          (no negativos en costos)
//
// Heurística de separadores:
//  - Múltiples "." sin "," → todos miles ("1.500.000" → "1500000.00")
//  - Múltiples "," sin "." → todos miles ("1,500,000" → "1500000.00")
//  - Mezcla "." + "," → el ÚLTIMO que aparece es el decimal, los demás miles
//    ("2.500.000,50" formato CR → "2500000.50"; "2,500,000.50" formato EN → idem)
//  - Un solo separador, ambigüedad:
//      tail = exactamente 3 dígitos AND head 1-3 dígitos → MILES
//        ("2.500" → "2500.00", "999.999" → "999999.00")
//      cualquier otro caso → DECIMAL, truncado a 2 dec
//        ("2.5" → "2.50", "2500000.5" → "2500000.50", "100.999" → "100.99")
//
// Trade-off conocido: "33.336" se interpreta como ₡33336 (miles), no como
// "₡33,33 con 1 dígito de basura". En es-CR el separador decimal es coma,
// así que un usuario que quiera escribir céntimos tipearía "33,336".
// Si en el futuro aparecen falsos positivos reales, considerar AmountInput
// con un separador explícito on-blur.

export function parseAmount(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;

  // Trim símbolo, espacios, NBSP. Conservamos solo dígitos, "." y ",".
  const cleaned = String(raw)
    .replace(/[^\d.,\-]/g, "")
    .trim();
  if (cleaned === "" || cleaned === "-") return null;

  // Sin negativos.
  if (cleaned.startsWith("-")) return null;

  const dotCount = (cleaned.match(/\./g) ?? []).length;
  const commaCount = (cleaned.match(/,/g) ?? []).length;
  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");

  let normalized: string;

  if (dotCount === 0 && commaCount === 0) {
    normalized = cleaned;
  } else if (dotCount > 0 && commaCount > 0) {
    // Ambos: el último que aparece es el decimal.
    const decIdx = Math.max(lastDot, lastComma);
    const head = cleaned.slice(0, decIdx).replace(/[.,]/g, "");
    const tail = cleaned.slice(decIdx + 1);
    normalized = `${head}.${tail}`;
  } else if (dotCount > 1) {
    // "1.500.000" → todos miles.
    normalized = cleaned.replace(/\./g, "");
  } else if (commaCount > 1) {
    // "1,500,000" → todos miles.
    normalized = cleaned.replace(/,/g, "");
  } else {
    // Exactamente un separador. Heurística:
    // - tail = 3 dígitos AND head 1-3 dígitos → miles ("2.500", "999.999")
    // - cualquier otro → decimal ("2.5", "33.336", "2500000.50")
    const sepIdx = dotCount === 1 ? lastDot : lastComma;
    const head = cleaned.slice(0, sepIdx);
    const tail = cleaned.slice(sepIdx + 1);

    if (tail.length === 3 && head.length >= 1 && head.length <= 3 && /^\d+$/.test(head)) {
      normalized = `${head}${tail}`;
    } else {
      normalized = `${head}.${tail}`;
    }
  }

  // Validar: ahora debe ser /^\d+(\.\d+)?$/
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;

  // Truncar a 2 decimales (no redondear: si el usuario escribió "33.336"
  // queremos "33.33", no "33.34", para que NO se exceda el monto que está
  // viendo en pantalla. El backend tampoco acepta más de 2 decimales.)
  const dotIdx = normalized.indexOf(".");
  if (dotIdx === -1) return `${normalized}.00`;

  const intPart = normalized.slice(0, dotIdx);
  const decPart = normalized
    .slice(dotIdx + 1)
    .slice(0, 2)
    .padEnd(2, "0");
  return `${intPart}.${decPart}`;
}
